import { qsStringify } from '../utils/index.js'

import { interceptor2Chain, transformBody, transformParams as transformParameters } from './util/index.js'

import { download, timeout } from './interceptor/index.js'

import { HttpError } from './error.js'

const MAX_INTERCEPTOR_ORDER = 10_000

function createInst(_default = {}) {
    const inst = async function (...parameters) {
        let request_
        request_ =
            typeof parameters[0] === 'string'
                ? {
                      ...parameters[2],
                      method: parameters[0],
                      url: parameters[1],
                  }
                : parameters[0] ?? {}

        if (!(request_.headers instanceof Headers)) {
            request_.headers = new Headers(request_.headers)
        }

        request_.url = request_.url?.trim()

        const result = await inst._interceptorChain(request_)
        if (result instanceof Blob) {
            return result
        }
        return request_.exposeResp ? result : result.data
    }

    inst._default = _default
    inst.MAX_INTERCEPTOR_ORDER = MAX_INTERCEPTOR_ORDER

    inst.get = function (url, parameters, init = {}) {
        return inst('get', url, {
            ...init,
            params: parameters,
        })
    }

    inst.post = function (url, body = {}, init = {}) {
        return inst('post', url, {
            ...init,
            body,
        })
    }

    inst.sse = function (url, parameters = {}, init = {}) {
        return inst('sse', url, {
            ...init,
            params: parameters,
            exposeResp: true,
        })
    }

    inst.ws = function (url) {
        return inst('ws', url, {
            exposeResp: true,
        })
    }

    function handleChain() {
        inst._interceptorChain = interceptor2Chain(
            [...(inst._default.interceptors ?? [])],
            (request_, next) =>
                next({
                    ...inst._default,
                    ...request_,
                }),
            _request,
            MAX_INTERCEPTOR_ORDER,
        )
    }

    handleChain()

    inst.use = (interceptor, options = {}) => {
        inst._default.interceptors ??= []
        inst._default.interceptors.push(Object.assign(interceptor, options))
        handleChain()
    }

    inst.extend = (_default2 = {}) => {
        return createInst({
            ...inst._default,
            ..._default2,
            interceptors: [...(inst._default.interceptors ?? []), ...(_default2.interceptors ?? [])],
        })
    }

    return inst
}

async function _request({
    url,
    method = 'get',
    params,
    body,
    reqType: requestType = 'json',
    respType,
    paramsSerializer: parametersSerializer = qsStringify,
    headers = new Headers(),

    baseURL = '/',
    ...init
}) {
    if (params) {
        url += (url.includes('?') ? '&' : '?') + transformParameters(params, parametersSerializer)
    }
    if (!/^(http(s?):)?\/\//.test(url)) {
        if (!baseURL.endsWith('/')) {
            baseURL = baseURL + '/'
        }

        if (url.startsWith('/')) {
            url = url.slice(1)
        }
        url = baseURL + url
    }

    const _init = {
        ...init,
        url,
        method: init.onUploadProgress ? 'post' : method.toLowerCase(),
        headers,
        body: transformBody(body, init.onUploadProgress ? 'form-data' : requestType, parametersSerializer, headers),
    }

    if (method === 'sse') {
        return new Promise((resolve, reject) => {
            const sse = new EventSource(url, _init)

            const listeners = []
            const returnValue = {
                subscribe(event_, handler = event_) {
                    if (handler === event_) {
                        event_ = 'message'
                    }

                    sse.addEventListener(event_, handler)
                    const index = listeners.length
                    const stop = () => {
                        sse.removeEventListener(event_, handler)
                        listeners[index] = undefined
                    }
                    listeners.push(stop)
                    return stop
                },
                close: () => {
                    for (const stop of listeners) stop?.()
                    sse.close()
                },
            }

            returnValue.subscribe('error', (error) => {
                returnValue.close()
                reject(error)
            })
            returnValue.subscribe('open', () => {
                resolve(returnValue)
            })
        })
    }

    if (method === 'ws') {
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(url)
            const listeners = []
            const returnValue = {
                send(data) {
                    ws.send(data)
                },
                subscribe(event_, handler = event_) {
                    if (handler === event_) {
                        event_ = 'message'
                    }

                    ws.addEventListener(event_, handler)
                    const index = listeners.length
                    const stop = () => {
                        ws.removeEventListener(event_, handler)
                        listeners[index] = undefined
                    }
                    listeners.push(stop)
                    return stop
                },
                close: () => {
                    try {
                        for (const stop of listeners) {
                            try {
                                stop?.()
                            } catch (error) {
                                console.debug(`[request] ws listener stop err`, error)
                            }
                        }
                        ws.close()
                    } catch (error) {
                        console.debug(`[request] ws close err`, error)
                    }
                },
            }

            returnValue.subscribe('error', (error) => {
                returnValue.close()
                reject(error)
            })
            returnValue.subscribe('open', () => {
                resolve(returnValue)
            })
            returnValue.subscribe('close', () => {
                returnValue.close()
            })
        })
    }

    let resp
    resp = await (_init.onUploadProgress
        ? new Promise((resolve) => {
              const xhr = new XMLHttpRequest()

              xhr.responseType = 'blob'

              xhr.addEventListener('readystatechange', () => {
                  if (xhr.readyState === 4) {
                      resolve(
                          new Response(xhr.response, {
                              headers: xhr
                                  .getAllResponseHeaders()
                                  .trim()
                                  .split(/[\n\r]+/)
                                  .map((line) => {
                                      const parts = line.split(': ')
                                      const header = parts.shift()
                                      const value = parts.join(': ')
                                      return [header, value]
                                  }),
                              status: xhr.status,
                              statusText: xhr.statusText,
                          }),
                      )
                  }
              })

              xhr.upload.addEventListener('progress', (e) => {
                  if (e.lengthComputable) {
                      _init.onUploadProgress(Math.ceil((e.loaded / e.total) * 100))
                  }
              })
              // eslint-disable-next-line unicorn/prefer-add-event-listener
              xhr.onerror = () => {
                  resolve(
                      new Response(xhr.response, {
                          headers: xhr.getAllResponseHeaders(),
                          status: xhr.status,
                          statusText: xhr.statusText,
                      }),
                  )
              }

              xhr.open(_init.method, _init.url, true)

              for (let [k, v] of _init.headers) {
                  xhr.setRequestHeader(k, v)
              }

              xhr.send(_init.body)
          })
        : fetch(url, _init))
    const _headers = resp.headers
    if (respType === 'text') {
        return resp
            .clone()
            .text()
            .then((_result) => {
                return {
                    headers: _headers,
                    resp,
                    data: _result,
                }
            })
    }
    if (respType === 'blob') {
        const _resp = resp.clone()
        return resp
            .blob()
            .catch((error) => {
                console.error('[http]: Response 解析为 Blob 出错', error)
                throw new HttpError({
                    code: 500,
                    status: false,
                    headers,
                    message: `Response 解析为 Blob 出错（详见Console）！`,
                })
            })
            .then((blob) => {
                const _blob = blob
                _blob.resp = _resp
                return _blob
            })
    }

    const contentType = _headers.get('Content-Type') ?? ''
    if (contentType.startsWith('application/json')) {
        return resp.json().then((_result) => {
            _result.headers = _headers
            return _result?.status === true || _result?.ok === true ? _result : Promise.reject(new HttpError(_result))
        })
    }

    throw new HttpError({
        code: resp.status ?? 500,
        status: false,
        headers,
        message: resp.statusText || '未知错误（response 非预期的 json）！',
    })
}

export const request = /*#__PURE__*/ createInst({
    interceptors: [
        { interceptor: download, order: MAX_INTERCEPTOR_ORDER + 100, name: 'download' },
        { interceptor: timeout, order: MAX_INTERCEPTOR_ORDER + 200, name: 'timeout' },
    ],
})
